/**
 * B606 (d) — the ticket page's controller: each token in the fragment is
 * opened through the PUBLIC door (`ticket.open`, one call per token, against
 * the tenant the token names — the server verifies), and each opened ticket's
 * QR is drawn here from the token itself. No session, no guest mint.
 */
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { createApiClient } from "../lib/api.js";
import { ticketQrDataUrl } from "../lib/qr.js";
import { tenantOfTicketToken, ticketPageTitle, type TicketFace, type TicketPageAnswer } from "../lib/ticketPage.js";
import { TicketPage } from "./ticketPage.js";

export function TicketApp(props: { readonly tokens: readonly string[]; readonly apiBaseUrl: string }): ReactElement {
  const [tickets, setTickets] = useState<readonly TicketFace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const open = useCallback(
    (isGone: () => boolean) => {
      setLoading(true);
      setError(undefined);
      void Promise.all(
        props.tokens.map(async (token): Promise<TicketFace> => {
          const tenantId = tenantOfTicketToken(token);
          if (tenantId === undefined) return { token };
          const body = (await createApiClient({ baseUrl: props.apiBaseUrl, tenantId }).call("ticket.open", { token })) as { page?: TicketPageAnswer };
          if (body.page === undefined) return { token };
          return { token, page: body.page, qr: await ticketQrDataUrl(token) };
        }),
      )
        .then((faces) => {
          if (!isGone()) setTickets(faces);
        })
        .catch(() => {
          if (!isGone()) setError("We couldn't reach the ticket service just now. Your QR codes are also in your ticket email.");
        })
        .finally(() => {
          if (!isGone()) setLoading(false);
        });
    },
    [props.tokens, props.apiBaseUrl],
  );

  const seller = tickets.find((t) => t.page?.seller !== undefined)?.page?.seller;
  useEffect(() => {
    document.title = ticketPageTitle(seller);
  }, [seller]);

  useEffect(() => {
    let gone = false;
    open(() => gone);
    return () => {
      gone = true;
    };
  }, [open]);

  return <TicketPage tickets={loading ? [] : tickets} loading={loading} error={error} onRetry={() => open(() => false)} />;
}
